<template>
<m-pageframe
    :title="title"
>
<v-container
    v-if="settings != null"
>

    <b>General</b><br/><br/>

    <m-edit-text
        label="Organization Name"
        :value="settings.organization_label"
        read_only
    />

    <m-edit-text
        label="Phone Number"
        :value="prettyPrintEndpoint(settings.endpoint)"
        read_only
    />

    <m-edit-text
        label="Team Name"
        :value="settings.group_label"
        read_only
    />

    <b>Voicemail</b><br/><br/>
    <audio controls :src="settings.voicemail_url_greeting" type="audio/ogg">
        Your browser does not support the audio element.
    </audio>

    <div
        style="margin-top: 5px; margin-left: 5px;"
    >
        <v-btn
            v-on:click="onClickUseDefaultGreeting"
        >
            Use default greeting...
        </v-btn>

        <v-btn
            color="primary"
            style="margin-left: 15px;"
            v-on:click="onClickUploadGreeting"
        >
            Upload new greeting...
        </v-btn>
    </div>

    <div style="margin-top: 15px;">
        Looking for an easy way to record an mp3 file? Check out <a href="https://online-voice-recorder.com/" target="_blank">this voice recording tool</a>
    </div>

    <m-edit-text
        label="Voicemail answer delay (in seconds)"
        v-model="settings.voicemail_delay_in_seconds"
        v-on:input="upsert"
        integer_positive_non_zero
        style="margin-top: 15px;"
    />

    <m-edit-blobs-dialog
        ref="attach-greeting-dialog"
        title="Upload Greeting"
        v-on:ok="afterUploadGreeting($event)"
        :externally_visible="true"
        :organization_id="this.$route.params.organization_id"
    />

    <div style="margin-top: 15px;">
    <b>Call Handling</b>
    </div>

    <v-select
        label="When a call is received"
        :items="modes"
        v-model="settings.voice_mode"
        v-on:change="upsert"
        style="margin-top: 15px;"
    />

    <m-autocomplete-phone-number
        label="Forward Call To"
        :value="forward_to_obj"
        disable_group_text
        v-if="settings.voice_mode == 'forward-to-one-number'"
        v-on:input="onInputForwardTo($event)"
    />

    <v-select
        label="When forwarding a call, what caller id would you like to come up?"
        :items="caller_id_modes"
        v-model="settings.inbound_caller_id_mode"
        v-if="settings.voice_mode == 'forward-to-one-number'"
        v-on:change="upsert"
        v-show="$rc.isFeatureEnabledSetInboundCallerIdMode()"
    />

    <v-select
        label="When a team member picks up"
        :items="pickup_options"
        v-model="settings.forward_to_fixed_number_use_digit_press_voicemail_detection"
        v-if="settings.voice_mode == 'forward-to-one-number'"
        v-on:change="upsert"
    />

</v-container>
</m-pageframe>
</template>

<script>

const E164 = require('@/js/E164.js');

export default {
    data() {
        return {
            settings: null,
            modes: [
                {'text': 'Play a message asking the caller to text instead', value: 'not-supported'},
                {'text': 'Send the caller directly to voicemail', value: 'voicemail-only'},
                {'text': 'Forward the phone call to one number (calls will forward 24 x 7)', value: 'forward-to-one-number'}
            ],

            caller_id_modes: [
                {'text' : 'Show all forwarded calls as coming from the team\'s RedChirp number', value: 'patch-call-from-red-chirp-number' },
                {'text' : 'Show all forwarded calls as coming from the caller\'s number', value: 'patch-call-from-callers-number' }
            ],

            pickup_options: [
                {'text': 'Connect the caller immediately', value: false },
                {'text': 'Require that a digit be pressed before the call is connected', value: true },
            ],
        }
    },


    mounted () {
        this.update();
    },

    methods: {

        update() {
            this.$rc.showSpinner();
            
            this.$rc.getTeamNumberVoiceSettings(
                this.$route.params.organization_id,
                this.$route.params.endpoint,
            
                (data) => {
                    this.$rc.hideSpinner();

                    this.settings = data;
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to get team number voice settings');
                },
            );
        },

        upsert()
        {
            this.$rc.showSpinner();
            
            this.$rc.putTeamNumberVoiceSettings(
                this.settings,
            
                (data) => {
                    this.$rc.hideSpinner();

                    this.settings = data;
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to update team number voice settings');
                },
            );
        },

        onInputForwardTo(value)
        {
            this.settings.forward_to_fixed_number = value.value;
            this.upsert();
        },

        prettyPrintEndpoint(endpoint)
        {
            return E164.prettyPrintUsPhoneNumber(endpoint);
        },

        onClickUseDefaultGreeting()
        {
            this.$rc.confirm(
                'Use default greeting',
                'Use the default (generic) voicemail greeting?  This will remove any custom greeting you may have recorded.',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Yes, use default greeting', 'value': 'confirm'},
                ],

                (response) => {
                    if (response == 'confirm') 
                    {
                        this.settings.voicemail_url_greeting = 'https://files.redchirp.com/default-voicemail-greeting.mp3';
                        this.upsert();
                    } 
                    else 
                    {
                        return;
                    }
                }

            );
        },

        onClickUploadGreeting()
        {
            this.$refs['attach-greeting-dialog'].showDialog();
        },

        afterUploadGreeting(blobs)
        {
            if ( blobs.length != 1 )
                alert('You must upload only one greeting');

            this.$rc.showSpinner();
            
            this.$rc.getBlobMetaDataMultiple(
                blobs,
            
                (data) => {
                    this.$rc.hideSpinner();

                    let meta_data = data.meta_data[0];

                    if ( meta_data.content_type != 'audio/mpeg' )
                    {
                        alert('You must upload a mp3 file');
                        return;
                    }
                    
                    this.settings.voicemail_url_greeting = 'https://files.redchirp.com/'+meta_data.object_name;
                    this.upsert();

                    alert('Greeting changed');
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to get voice greeting meta data');
                },
            );
        },

    },

    computed: {
        title() {
            let settings = this.settings;

            if ( settings == null ) return 'Team Number Voice Settings';

            return settings.organization_label+': '+settings.group_label+' Voice Settings';
        },

        forward_to_obj()
        {
            let settings = this.settings;

            if ( settings == null ) return null;
            if ( settings.forward_to_fixed_number == null ) return null;

            return {
                'text' : E164.prettyPrintUsPhoneNumber(settings.forward_to_fixed_number),
                'value' : settings.forward_to_fixed_number,
            };
        }
    },
}
</script>

<style lang="scss" scoped>
</style>